'use client'

import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { Command } from 'cmdk'
import { cn } from '../utils/styles'
import { Country, COUNTRIES, getCountriesByName } from '../utils/countries'

export interface CountrySelectorProps {
  value?: string | string[]
  onValueChange: (value: string | string[]) => void
  multiple?: boolean
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
  maxSelectedDisplay?: number
  countries?: Country[]
  showFlags?: boolean
  showCodes?: boolean
  size?: 'sm' | 'md' | 'lg'
}

interface BadgeProps {
  children: React.ReactNode
  variant?: 'default' | 'secondary' | 'destructive' | 'outline'
  className?: string
  onRemove?: () => void
}

const Badge = ({ children, variant = 'secondary', className, onRemove }: BadgeProps) => {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/80',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/80',
    outline: 'text-foreground border border-border bg-background hover:bg-accent',
  }

  return (
    <div className={cn(
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      variants[variant],
      className
    )}>
      {children}
      {onRemove && (
        <button
          type="button"
          className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={onRemove}
        >
          <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
        </button>
      )}
    </div>
  )
}

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
    size?: 'default' | 'sm' | 'lg' | 'icon'
  }
>(({ className, variant = 'default', size = 'default', ...props }, ref) => {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  }

  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  }

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = 'Button'

const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Input.displayName = 'Input'

export function CountrySelector({
  value,
  onValueChange,
  multiple = false,
  placeholder = multiple ? 'Select countries...' : 'Select country...',
  searchPlaceholder = 'Search countries...',
  disabled = false,
  className,
  maxSelectedDisplay = 3,
  countries = COUNTRIES,
  showFlags = true,
  showCodes = true,
  size = 'md',
}: CountrySelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const selectedValues = React.useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : []
    }
    return typeof value === 'string' ? [value] : []
  }, [value, multiple])

  const selectedCountries = React.useMemo(() => {
    return countries.filter(country => selectedValues.includes(country.code))
  }, [countries, selectedValues])

  const filteredCountries = React.useMemo(() => {
    if (!searchQuery) return countries
    return getCountriesByName(searchQuery).filter(country =>
      countries.some(c => c.code === country.code)
    )
  }, [searchQuery, countries])

  const handleSelect = React.useCallback(
    (countryCode: string) => {
      if (multiple) {
        const newSelection = selectedValues.includes(countryCode)
          ? selectedValues.filter(code => code !== countryCode)
          : [...selectedValues, countryCode]
        onValueChange(newSelection)
      } else {
        onValueChange(countryCode)
        setOpen(false)
      }
    },
    [multiple, selectedValues, onValueChange]
  )

  const handleRemove = React.useCallback(
    (countryCode: string) => {
      if (multiple) {
        const newSelection = selectedValues.filter(code => code !== countryCode)
        onValueChange(newSelection)
      } else {
        onValueChange('')
      }
    },
    [multiple, selectedValues, onValueChange]
  )

  const displayText = React.useMemo(() => {
    if (selectedCountries.length === 0) {
      return placeholder
    }

    if (!multiple) {
      const country = selectedCountries[0]
      return (
        <div className="flex items-center gap-2">
          {showFlags && <span className="text-lg">{country.flag}</span>}
          <span>{country.name}</span>
          {showCodes && <span className="text-muted-foreground text-xs">({country.code})</span>}
        </div>
      )
    }

    if (selectedCountries.length <= maxSelectedDisplay) {
      return (
        <div className="flex flex-wrap gap-1">
          {selectedCountries.map(country => (
            <Badge
              key={country.code}
              variant="secondary"
              className="gap-1 pr-1"
              onRemove={() => handleRemove(country.code)}
            >
              {showFlags && <span className="text-sm">{country.flag}</span>}
              <span className="text-xs">{country.name}</span>
              {showCodes && <span className="text-xs text-muted-foreground">({country.code})</span>}
            </Badge>
          ))}
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2">
        <span>{selectedCountries.length} countries selected</span>
        <Badge variant="secondary" className="text-xs">
          {selectedCountries.length}
        </Badge>
      </div>
    )
  }, [selectedCountries, placeholder, multiple, maxSelectedDisplay, handleRemove, showFlags, showCodes])

  const sizeClasses = {
    sm: 'h-8 text-sm',
    md: 'h-10',
    lg: 'h-12 text-lg',
  }

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between',
            sizeClasses[size],
            selectedCountries.length === 0 && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          <div className="flex-1 text-left overflow-hidden">
            {displayText}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            'z-50 w-72 rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
          )}
          align="start"
        >
          <Command className="max-h-96">
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-none focus-visible:ring-0"
              />
            </div>
            <Command.List className="max-h-[300px] overflow-y-auto">
              {filteredCountries.length === 0 ? (
                <Command.Empty className="py-6 text-center text-sm">
                  No countries found.
                </Command.Empty>
              ) : (
                <Command.Group>
                  {filteredCountries.map((country) => {
                    const isSelected = selectedValues.includes(country.code)
                    return (
                      <Command.Item
                        key={country.code}
                        value={`${country.code} ${country.name}`}
                        onSelect={() => handleSelect(country.code)}
                        className="relative flex cursor-pointer select-none items-center gap-3 rounded-sm px-2 py-3 text-sm outline-none hover:bg-accent hover:text-accent-foreground aria-selected:bg-accent aria-selected:text-accent-foreground"
                      >
                        {showFlags && <span className="text-lg">{country.flag}</span>}
                        <div className="flex-1">
                          <div className="font-medium">{country.name}</div>
                          {showCodes && (
                            <div className="text-xs text-muted-foreground">
                              {country.code}
                            </div>
                          )}
                        </div>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </Command.Item>
                    )
                  })}
                </Command.Group>
              )}
            </Command.List>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export type { Country }